import axios from 'utils/axios';

import type {
  CuestionarioApiPaginated,
  CuestionarioConfigApiResponse,
  CuestionarioDraftUpsertPayload,
  CuestionarioPlantillaResumen,
  CuestionarioVersionHistoryResponse
} from '../types';

type CatalogoItem = { id: number; tipo: string; codigo: string | null; nombre: string; activo: boolean; orden: number };
type GrupoSelectItem = { id: number; sigla: string; nombre: string };

export const cuestionariosApi = {
  list: async (params?: {
    q?: string;
    estado?: 'borrador' | 'terminado' | 'archivado';
    nivel_dd_codigo?: string;
    grupo_id?: number;
    grupo_ids?: string;
    per_page?: number;
    page?: number;
    sort_by?: 'id' | 'nombre' | 'created_at';
    sort_dir?: 'asc' | 'desc';
  }): Promise<CuestionarioApiPaginated> => {
    const response = await axios.get('/api/v1/cuestionarios', { params });
    return response.data as CuestionarioApiPaginated;
  },

  create: async (payload: {
    nombre: string;
    descripcion?: string | null;
    tipo_tercero_id: number;
    sector_id?: number | null;
    idioma_predeterminado_id: number;
    idiomas: number[];
    grupos: number[];
    paises?: number[];
    propietarios: number[];
    responsables?: number[];
    analisis_riesgo?: boolean;
    nivel_dd_id: number;
    region_id?: number | null;
    tipo_figura_id: number;
    meta?: Record<string, unknown>;
    plantilla_id?: string | null;
  }): Promise<{ id: number; uuid: string }> => {
    const response = await axios.post('/api/v1/cuestionarios', payload);
    return response.data;
  },

  update: async (
    cuestionarioId: string,
    payload: Partial<{
      nombre: string;
      descripcion: string | null;
      tipo_tercero_id: number;
      sector_id: number | null;
      idioma_predeterminado_id: number;
      idiomas: number[];
      grupos: number[];
      nivel_dd_id: number;
      region_id: number | null;
      tipo_figura_id: number;
    }>
  ): Promise<{ id: number; uuid: string }> => {
    const response = await axios.put(`/api/v1/cuestionarios/${cuestionarioId}`, payload);
    return response.data;
  },

  getConfig: async (cuestionarioId: string, params?: { versionId?: number; idiomaId?: number }): Promise<CuestionarioConfigApiResponse> => {
    const response = await axios.get(`/api/v1/cuestionarios/${cuestionarioId}/config`, {
      params: {
        version_id: params?.versionId,
        idioma_id: params?.idiomaId
      }
    });
    return (response.data?.data ?? response.data) as CuestionarioConfigApiResponse;
  },

  upsertDraft: async (cuestionarioId: string, payload: CuestionarioDraftUpsertPayload): Promise<CuestionarioConfigApiResponse> => {
    const response = await axios.put(`/api/v1/cuestionarios/${cuestionarioId}/draft`, payload);
    return (response.data?.data ?? response.data) as CuestionarioConfigApiResponse;
  },

  publish: async (cuestionarioId: string): Promise<CuestionarioConfigApiResponse> => {
    const response = await axios.post(`/api/v1/cuestionarios/${cuestionarioId}/publish`);
    return (response.data?.data ?? response.data) as CuestionarioConfigApiResponse;
  },

  createVersion: async (cuestionarioId: string): Promise<CuestionarioConfigApiResponse> => {
    const response = await axios.post(`/api/v1/cuestionarios/${cuestionarioId}/versions`);
    return (response.data?.data ?? response.data) as CuestionarioConfigApiResponse;
  },

  listVersions: async (cuestionarioId: string): Promise<CuestionarioVersionHistoryResponse> => {
    const response = await axios.get(`/api/v1/cuestionarios/${cuestionarioId}/versions`);
    return (response.data?.data ? response.data : { data: response.data }) as CuestionarioVersionHistoryResponse;
  },

  getVersionSnapshot: async (cuestionarioId: string, versionId: number, params?: { idiomaId?: number }): Promise<CuestionarioConfigApiResponse> => {
    const response = await axios.get(`/api/v1/cuestionarios/${cuestionarioId}/versions/${versionId}/snapshot`, {
      params: { idioma_id: params?.idiomaId }
    });
    return (response.data?.data ?? response.data) as CuestionarioConfigApiResponse;
  },

  addComment: async (cuestionarioId: string, versionId: number, payload: { comentario: string; idioma_id?: number }): Promise<CuestionarioConfigApiResponse> => {
    const response = await axios.post(`/api/v1/cuestionarios/${cuestionarioId}/versions/${versionId}/comments`, payload);
    return (response.data?.data ?? response.data) as CuestionarioConfigApiResponse;
  },

  listCatalogosByTipo: async (tipo: string): Promise<{ success: boolean; data: CatalogoItem[] }> => {
    const response = await axios.get(`/api/v1/catalogos/tipo/${tipo}`);
    return response.data;
  },

  listGruposSelect: async (): Promise<{ success: boolean; data: GrupoSelectItem[] }> => {
    const response = await axios.get('/api/v1/grupos/select');
    return response.data;
  },

  listPlantillas: async (params?: { q?: string }): Promise<{ success: boolean; data: CuestionarioPlantillaResumen[] }> => {
    const response = await axios.get('/api/v1/cuestionarios/plantillas', { params: { q: params?.q } });
    return response.data;
  },

  archive: async (cuestionarioId: string): Promise<{ id: number; uuid: string; estado: string }> => {
    const response = await axios.post(`/api/v1/cuestionarios/${cuestionarioId}/archive`);
    return response.data;
  }
};
